import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { authService } from '../services/auth';
import { subscriptionService } from '../services/subscription';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import Footer from '../components/Footer';

const Auth = ({ mode = 'login' }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await authService.login({
        email: formData.email,
        password: formData.password,
      });

      if (response.success) {
        const user = response.data.user;
        toast({
          title: "¡Bienvenido!",
          description: `Hola ${user.name}`,
        });
        
        // Redirigir según el rol del usuario
        if (user.role === 'admin') {
          navigate('/admin');
        } else if (user.role === 'tienda') {
          // TODO: Implementar dashboard para tiendas
          navigate('/home');
        } else {
          // Para usuarios cliente, verificar suscripción antes de redirigir
          try {
            const statusResponse = await subscriptionService.getStatus();
            const canAccessPremium = 
              statusResponse?.data?.can_access_premium ?? 
              statusResponse?.data?.has_active_subscription ?? 
              false;
            
            if (canAccessPremium) {
              navigate('/home');
            } else {
              // No tiene suscripción activa - redirigir a pantalla de pago
              navigate('/subscription');
            }
          } catch (error) {
            // Si hay error verificando suscripción, redirigir a pago por seguridad
            console.warn('Error verificando suscripción en login:', error);
            navigate('/subscription');
          }
        }
      }
    } catch (err) {
      const errorMessage = err.message || 'Error al iniciar sesión. Verifica tus credenciales.';
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignupStep1 = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Validar que todos los campos estén llenos
    if (!formData.name || !formData.email || !formData.phone || !formData.password) {
      setError('Por favor completa todos los campos');
      setLoading(false);
      return;
    }

    // Validar contraseña mínima
    if (formData.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      setLoading(false);
      return;
    }

    // Validar que se hayan aceptado los términos y condiciones
    if (!acceptedTerms) {
      setError('Debes aceptar los términos y condiciones y la política de privacidad para continuar');
      setLoading(false);
      return;
    }

    try {
      // Obtener promoter_id del localStorage si existe
      const promoterId = localStorage.getItem('promoter_id');
      
      // Registrar usuario (sin pago)
      const userData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        ...(promoterId && { promoter_id: promoterId }), // Agregar promoter_id si existe
      };
      
      const registerResponse = await authService.register(userData);

      if (!registerResponse || !registerResponse.success) {
        throw new Error(registerResponse?.error || 'Error al registrar usuario');
      }

      // Éxito - redirigir a home (que verificará la suscripción)
      toast({
        title: "¡Cuenta creada!",
        description: `Bienvenido ${registerResponse.data.user.name}`,
      });

      const user = registerResponse.data.user;
      
      // Redirigir según el rol
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/home');
      }
    } catch (err) {
      console.error('❌ Error en registro:', err);
      const errorMessage = err.message || 'Error al registrar usuario. Intenta nuevamente.';
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };


  const handleGoogleLogin = () => {
    // Mock Google login
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('userName', 'Usuario');
    navigate('/home');
  };

  return (
    <div className="min-h-screen bg-black px-6 py-8 flex flex-col">
      <div className="max-w-md mx-auto flex-1">
        {/* Back button */}
        <button 
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-white hover:text-[#A2A2A2] transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Volver</span>
        </button>

        {/* Login Form */}
        {mode === 'login' && step === 1 && (
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Iniciar sesión</h1>
              <p className="text-[#A2A2A2]">Bienvenido de vuelta</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">Correo electrónico</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-white">Contraseña</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                  required
                />
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-2xl p-4">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#24b183] text-white hover:bg-[#1f9a72] rounded-full py-6 text-lg font-semibold transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {loading ? 'Iniciando sesión...' : 'Entrar'}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-white/10" />
              </div>
              {/* <div className="relative flex justify-center text-sm">
                <span className="bg-black px-4 text-[#A2A2A2]">o continúa con</span>
              </div> */}
            </div>

            {/* <Button
              type="button"
              onClick={handleGoogleLogin}
              variant="outline"
              className="w-full border-white/10 text-white bg-white/5 hover:bg-white/10 rounded-full py-6 text-lg font-medium transition-all hover:scale-105 active:scale-95"
            >
              Google
            </Button> */}

            <p className="text-center text-[#A2A2A2]">
              <button 
                onClick={() => navigate('/forgot-password')} 
                className="text-white hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </p>
            <p className="text-center text-[#A2A2A2]">
              ¿No tienes cuenta?{' '}
              <button onClick={() => navigate('/signup')} className="text-white hover:underline">
                Regístrate
              </button>
            </p>
          </div>
        )}

        {/* Signup Step 1: Create Account */}
        {mode === 'signup' && step === 1 && (
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Crear cuenta</h1>
              <p className="text-[#A2A2A2]">Empieza a disfrutar tus beneficios</p>
            </div>

            <form onSubmit={handleSignupStep1} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white">Nombre completo</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">Correo electrónico</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-white">Teléfono</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-white">Contraseña</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                  required
                  minLength={8}
                />
                <p className="text-[#A2A2A2] text-xs">Mínimo 8 caracteres</p>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-2xl p-4">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <div className="flex items-start gap-3">
                <Checkbox
                  id="accept-terms"
                  checked={acceptedTerms}
                  onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
                  className="mt-1 border-white/30 data-[state=checked]:bg-white data-[state=checked]:border-white data-[state=checked]:text-black"
                />
                <Label
                  htmlFor="accept-terms"
                  className="text-sm text-[#A2A2A2] leading-relaxed cursor-pointer flex-1"
                >
                  Acepto{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/terms');
                    }}
                    className="text-[#3B82F6] hover:text-[#2563EB] hover:underline"
                  >
                    términos y condiciones
                  </button>
                  {' '}y{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/privacy');
                    }}
                    className="text-[#3B82F6] hover:text-[#2563EB] hover:underline"
                  >
                    política de privacidad de datos
                  </button>
                </Label>
              </div>

              <Button
                type="submit"
                disabled={loading || !acceptedTerms}
                className="w-full bg-[#24b183] text-white hover:bg-[#1f9a72] rounded-full py-6 text-lg font-semibold transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {loading ? 'Procesando...' : 'Entrar'}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-white/10" />
              </div>
              {/* <div className="relative flex justify-center text-sm">
                <span className="bg-black px-4 text-[#A2A2A2]">o continúa con</span>
              </div> */}
            </div>

            {/* <Button
              type="button"
              onClick={handleGoogleLogin}
              variant="outline"
              className="w-full border-white/10 text-white bg-white/5 hover:bg-white/10 rounded-full py-6 text-lg font-medium transition-all hover:scale-105 active:scale-95"
            >
              Google
            </Button> */}
          </div>
        )}

        {/* Los pasos de plan y pago fueron removidos - ahora el registro solo crea la cuenta */}
      </div>
      <Footer className="mt-auto" />
      <Toaster />
    </div>
  );
};

export default Auth;
